/**
 * The concept's nav glyphs, transcribed path-for-path from
 * `Dashboard Concept - Merged.dc.html`.
 *
 * Inline SVG rather than a package: these are nine fixed marks at one size,
 * and `ionicons` — the thing they replace — shipped a whole web-component
 * runtime to draw them. `stroke-width: 1.8` at 16px is the concept's own
 * weight; lucide's 2 at 24 reads noticeably heavier next to Archivo.
 */
const Svg: React.FC<{ size?: number; children: React.ReactNode }> = ({
  size = 16,
  children,
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className="shrink-0"
  >
    {children}
  </svg>
);

export type IconProps = { size?: number };

export const OverviewIcon: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <path d="M3 11l9-7 9 7v9a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z" />
  </Svg>
);

export const AvatarIcon: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <circle cx="12" cy="4.5" r="2" />
    <path d="M12 7v6M6 10l6-2 6 2M12 13l-3 8M12 13l3 8" />
  </Svg>
);

export const MeasurementsIcon: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <path d="M3 8h18v8H3zM7 8v3M11 8v4M15 8v3M19 8v4" />
  </Svg>
);

export const CompositionIcon: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <rect x="3" y="9" width="18" height="6" rx="1.5" />
    <path d="M9 9v6M15 9v6" />
  </Svg>
);

export const HistoryIcon: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <path d="M3 20h18M4 16l5-6 4 3 7-8" />
  </Svg>
);

export const GoalIcon: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="8" />
    <circle cx="12" cy="12" r="3.5" />
    <circle cx="12" cy="12" r="0.6" />
  </Svg>
);

export const NotesIcon: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <rect x="5" y="3" width="14" height="18" rx="2" />
    <path d="M9 8h6M9 12h6M9 16h3" />
  </Svg>
);

export const PhotosIcon: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <path d="M4 8h3l2-3h6l2 3h3v11H4z" />
    <circle cx="12" cy="13" r="3.5" />
  </Svg>
);

export const SettingsIcon: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="3" />
    <path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M4.9 19.1L7 17M17 7l2.1-2.1" />
  </Svg>
);

/** Three dots: the phone top bar's overflow menu. */
export const MoreIcon: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <circle cx="5" cy="12" r="1.2" />
    <circle cx="12" cy="12" r="1.2" />
    <circle cx="19" cy="12" r="1.2" />
  </Svg>
);

export const BackIcon: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <path d="M15 5l-7 7 7 7" />
  </Svg>
);

/* ── Capture flow ─────────────────────────────────────────────────────────
   Replacing the ionicons of the same name. Drawn at the concept's 1.8 stroke
   so they sit with the nav glyphs rather than looking borrowed. */

export const CameraIcon: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <path d="M4 8h3l2-3h6l2 3h3v11H4z" />
    <circle cx="12" cy="13" r="3.5" />
  </Svg>
);

export const ChevronForwardIcon: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <path d="M9 5l7 7-7 7" />
  </Svg>
);

export const ArrowForwardIcon: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <path d="M4 12h15M13 6l6 6-6 6" />
  </Svg>
);

export const RefreshIcon: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <path d="M20 12a8 8 0 1 1-2.6-5.9M20 4v4h-4" />
  </Svg>
);

export const SaveIcon: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <path d="M5 4h11l3 3v13H5z" />
    <path d="M9 4v5h6V4M8 20v-6h8v6" />
  </Svg>
);

export const CaretUpIcon: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <path d="M6 15l6-6 6 6" />
  </Svg>
);

export const CaretDownIcon: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <path d="M6 9l6 6 6-6" />
  </Svg>
);

export const BodyIcon: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <circle cx="12" cy="4.5" r="2" />
    <path d="M12 7v6M6 10l6-2 6 2M12 13l-3 8M12 13l3 8" />
  </Svg>
);

export const WalkIcon: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <circle cx="13" cy="4" r="2" />
    <path d="M11 21l2-6-3-3 1-5 3 3 3 1M10 12l-2 4" />
  </Svg>
);

export const ClientsIcon: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <circle cx="9" cy="8" r="3.2" />
    <path d="M3 20c0-3.3 2.7-5.5 6-5.5s6 2.2 6 5.5" />
    <path d="M16 5.2a3.2 3.2 0 0 1 0 5.9M18 14.9c2 .8 3.4 2.6 3.4 5.1" />
  </Svg>
);

export const SearchIcon: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </Svg>
);

export const SendIcon: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <path d="M21 3 10 14" />
    <path d="M21 3l-6.5 18-4-8-8-4z" />
  </Svg>
);

export const CheckIcon: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <path d="M20 6 9 17l-5-5" />
  </Svg>
);
